import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireAuth } from "@/auth/middleware";
import { getCurrentUserId, getDb, schema } from "@/db/client";
import { sanitizeForSeroval } from "@/server/seroval-safe";
import type { Ratecard } from "@/calc";
import { ratecard as seedRatecard } from "./mocks";

/**
 * Хранение рейткарты.
 *
 * Одна строка на инстанс: это прайс агентства, а не настройка пользователя.
 * Читают и правят все, у кого открыт раздел (0010_ratecard.sql) — Braining
 * маленькие и согласование правки прайса руководителем не просили.
 *
 * Первое чтение сеет справочник из `mocks/ratecard.json` — того самого, что
 * скрипт собрал из книг Braining. Дублировать 32 роли в SQL значит завести
 * второй источник правды, который разъедется с первым.
 */

/**
 * Версия — дата последней правки.
 *
 * Не счётчик и не хеш: её единственная работа — дать человеку сказать «эта
 * смета по августовской рейткарте». Сравнивать ставки по версии нельзя, и
 * система этого нигде не делает: где важно, сравниваются сами числа.
 */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const RatecardPayload = z.object({
  data: z.record(z.string(), z.unknown()),
});

const _loadRatecard = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .handler(async (): Promise<Ratecard> => {
    const db = getDb();
    const [row] = await db.select({ data: schema.ratecard.data }).from(schema.ratecard);
    if (row) return sanitizeForSeroval("loadRatecard", row.data as Ratecard);

    const [seeded] = await db
      .insert(schema.ratecard)
      .values({ data: seedRatecard, updatedBy: getCurrentUserId() })
      .returning({ data: schema.ratecard.data });
    return sanitizeForSeroval("loadRatecard", seeded.data as Ratecard);
  });

const _saveRatecard = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => RatecardPayload.parse(d))
  .handler(async ({ data }): Promise<Ratecard> => {
    const db = getDb();
    const next = { ...(data.data as unknown as Ratecard), version: today() };

    const [saved] = await db
      .insert(schema.ratecard)
      .values({ data: next, updatedBy: getCurrentUserId() })
      .onConflictDoUpdate({
        target: schema.ratecard.singleton,
        set: { data: next, updatedBy: getCurrentUserId(), updatedAt: new Date().toISOString() },
      })
      .returning({ data: schema.ratecard.data });

    // RLS вернёт ноль строк, если правки не разрешены: молча «сохранить» в
    // никуда хуже, чем сказать правду.
    if (!saved) throw new Response("Forbidden", { status: 403 });
    return sanitizeForSeroval("saveRatecard", saved.data as Ratecard);
  });

export const loadRatecard = _loadRatecard;
export const saveRatecard = _saveRatecard;
